import { CSSProperties, useEffect, useState } from 'react';
import { Box, Button } from '@material-ui/core';
import {
  SText,
  SSelectDropdown,
  SelectDetailsButton,
  SelectMenuHeader,
  SEmptyState,
  SInfiniteScroll,
  SingleSelectLoader,
  SHighlightText
} from '@setvi/shared/components';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import eventEmitter from '@setvi/shared/libs/EventEmitter';
import { TextColor } from '@setvi/shared/enums';
import { PriceGroup } from '@setvi/shared/interfaces';
import { usePriceGroups } from './usePriceGroup';

interface PriceGroupDropdownProps {
  selectedPriceGroup: PriceGroup;
  onChange: (priceGroup: PriceGroup) => void;
  style?: { input?: CSSProperties; dropdownWrapperWidth?: number };
}

export const PriceGroupDropdown = ({
  selectedPriceGroup,
  style,
  onChange
}: PriceGroupDropdownProps) => {
  const [anchor, setAnchor] = useState(null);
  const [search, setSearch] = useState('');
  const { priceGroups, loading, fetchMore } = usePriceGroups({
    searchStr: search
  });

  const handleSelectCustomer = (priceGroup: PriceGroup) => {
    onChange(priceGroup);
    setAnchor(null);
  };

  const isSelected = (priceGroup: PriceGroup) =>
    selectedPriceGroup?._id === priceGroup._id;

  useEffect(() => {
    eventEmitter.on('sdropdown-menu-opened', () => setAnchor(null));

    return () => {
      eventEmitter.off('sdropdown-menu-opened', () => setAnchor(null));
    };
  }, []);

  return (
    <SSelectDropdown
      newDesign
      anchor={
        <SelectDetailsButton
          newDesign
          width={250}
          style={style?.input}
          title={
            selectedPriceGroup ? selectedPriceGroup.name : `Select Price Group`
          }
          titleHighligted={!!selectedPriceGroup}
          onClick={e => setAnchor(e?.currentTarget)}
          open={Boolean(anchor)}
        />
      }
      disablePortal
      anchorElement={anchor}
      onClose={() => setAnchor(null)}
      content={
        <Box width={style?.dropdownWrapperWidth || 285}>
          <SelectMenuHeader
            title="Select Price Group"
            searchValue={search}
            clearButton={!!selectedPriceGroup}
            handleClear={() => onChange(null)}
            handleChange={setSearch}
          />
          <Box height={300} overflow="auto">
            <Box>
              <SInfiniteScroll
                list={priceGroups}
                keyGetter={priceGroup => priceGroup._id}
                fetchMore={fetchMore}
                render={priceGroup => (
                  <Button
                    fullWidth
                    style={{
                      display: 'flex',
                      justifyContent: 'start',
                      color: isSelected(priceGroup) ? TextColor.Blue : undefined
                    }}
                    onClick={() => handleSelectCustomer(priceGroup)}
                    endIcon={
                      <CheckRoundedIcon
                        htmlColor={
                          isSelected(priceGroup)
                            ? TextColor.Blue
                            : 'transparent'
                        }
                      />
                    }
                    variant="text"
                    color="default">
                    <Box display="flex" flexDirection="column" width="100%">
                      <SHighlightText
                        value={priceGroup.name}
                        searchQuery={search}
                      />
                      <SText size="xs" color="primary" title={priceGroup._id} />
                    </Box>
                  </Button>
                )}
              />
            </Box>
            {loading && <SingleSelectLoader />}
            {!loading && priceGroups?.length === 0 && (
              <SEmptyState subTitle="No Price Group found." />
            )}
          </Box>
        </Box>
      }
    />
  );
};
